/**
 * Desktop ⇄ mobile route mapping — the single source of truth for the viewport
 * switch in BOTH directions (mobile-redirect.tsx on the desktop tree, and the
 * reverse guard in app/(mobile)/layout.tsx).
 *
 * ⚠️ This module MUST stay import-free of screen components. It is pulled into
 * the desktop bundle by MobileRedirect; importing a mobile screen here would
 * drag the entire `/m` tree into every desktop page's chunk.
 */

/**
 * Shared with hooks/use-mobile.ts (`useIsMobile`). Both sides of the switch
 * MUST agree on this number or a device sitting exactly on the boundary
 * bounces between trees forever.
 */
export const MOBILE_BREAKPOINT = 768

/** sessionStorage key for the "Use desktop site" escape hatch. */
export const FORCE_DESKTOP_KEY = "dentex_force_desktop"

/**
 * sessionStorage key holding the desktop path a user was bounced off because it
 * has no mobile equivalent. Written by MobileRedirect, read-and-cleared exactly
 * once by <OutOfScopeToast /> after it lands on `/m`.
 *
 * sessionStorage (not a query param or router state) because the hop is a
 * `router.replace` across two route *groups* — the desktop tree unmounts
 * entirely, so React state doesn't survive, and a `?from=` param would stick in
 * the URL and re-fire the toast on every back-navigation.
 */
export const OUT_OF_SCOPE_KEY = "dentex_m_out_of_scope"

/** The four tab roots plus the modal-ish stacks that hang off them. */
export const MOBILE_HOME = "/m"

/**
 * Desktop path prefix → mobile equivalent. Ordered longest-prefix-first at
 * match time, so `/leads/recycle` and `/leads` can coexist.
 *
 * Anything NOT listed has no mobile equivalent (hr, reports, settings,
 * telephony, inventory…) and falls back to MOBILE_HOME — Wave 3 adds the
 * "available on desktop only" toast on arrival.
 */
export const DESKTOP_TO_MOBILE: Record<string, string> = {
  "/leads": "/m/patients",
  "/clients": "/m/patients",
  "/kartele": "/m/patients",
  "/patients": "/m/patients",
  "/calendar": "/m/appointments",
  "/appointments": "/m/appointments",
  "/quotes": "/m/billing",
  "/invoices": "/m/billing",
  "/payments": "/m/billing",
  "/billing": "/m/billing",
  "/inbox": "/m/inbox",
  "/lab-orders": "/m/bench",
  // NOTE: the desktop home lives at "/" (app/(dashboard)/page.tsx). There is no
  // "/dashboard" route — "/dashboard" is kept only as a defensive alias so a
  // stale bookmark still lands somewhere sane instead of Next's 404.
  "/notifications": "/m/notifications",
  "/dashboard": MOBILE_HOME,
  "/": MOBILE_HOME,
}

/**
 * Mobile path prefix → desktop equivalent, for the ≥768px reverse guard.
 * Deliberately NOT derived from DESKTOP_TO_MOBILE: that map is many-to-one
 * (leads/clients/kartele all collapse to /m/patients) so the reverse needs an
 * explicit "preferred" desktop landing per mobile route.
 */
export const MOBILE_TO_DESKTOP: Record<string, string> = {
  "/m/patients": "/clients",
  "/m/appointments": "/calendar",
  "/m/billing": "/invoices",
  "/m/inbox": "/inbox",
  "/m/bench": "/lab-orders",
  // The driver's screen has a desktop twin at /logistics/runs. Without this
  // entry a wide viewport falls through to "/", which redirects a driver back
  // here — an infinite ping-pong between the two trees.
  "/m/runs": "/logistics/runs",
  "/m/notifications": "/notifications",
  "/m/book": "/calendar",
  "/m/invoice/new": "/invoices",
  "/m": "/",
}

function longestPrefixMatch(
  pathname: string,
  table: Record<string, string>
): string | null {
  const keys = Object.keys(table).sort((a, b) => b.length - a.length)
  for (const key of keys) {
    if (key === "/") continue
    if (pathname === key || pathname.startsWith(`${key}/`)) return table[key]
  }
  return table[pathname] ?? null
}

/**
 * Desktop pages that sit beside `/leads/[id]`. Next matches these static
 * segments before the dynamic one, so every OTHER `/leads/<x>` is a lead id.
 * A new page under app/(dashboard)/leads must be added here AND to the
 * pre-hydration script in app/layout.tsx — scripts/mobile-deep-links.test.mjs
 * fails until both list it.
 */
export const LEAD_STATIC_PAGES: readonly string[] = ["new", "recycle", "italian"]

/**
 * Record-level links, checked before the prefix tables: a URL that names ONE
 * record opens that record on the other side, not just the list it lives in
 * (a lead link from a push or a chat used to land on the patients list).
 * Returns null for list-level URLs, which keep the table fallback.
 *
 * The id spaces match 1:1 — `/m/patients/[id]` is lead-keyed, `/m/inbox/[id]`
 * takes the same conversation id as the desk's `?conversation=`. `/clients`
 * has no detail page of its own (client rows open `/leads/<id>`), so it stays
 * list-level.
 */
function toMobileDetail(pathname: string, search: string): string | null {
  const [section, id] = pathname.split("/").filter(Boolean)
  if (section === "leads" && id && !LEAD_STATIC_PAGES.includes(id)) {
    return `/m/patients/${id}`
  }

  const params = new URLSearchParams(search)
  const conversation = params.get("conversation")
  if (pathname === "/inbox" && conversation) {
    return `/m/inbox/${encodeURIComponent(conversation)}`
  }
  const appointment = params.get("appointment")
  if ((pathname === "/appointments" || pathname === "/calendar") && appointment) {
    return `/m/appointments?appointment=${encodeURIComponent(appointment)}`
  }
  return null
}

/** Mobile side of toMobileDetail. */
function toDesktopDetail(pathname: string, search: string): string | null {
  const [root, section, id] = pathname.split("/").filter(Boolean)
  if (root !== "m") return null

  if (section === "patients" && id) return `/leads/${id}`
  if (section === "inbox" && id) {
    return `/inbox?${new URLSearchParams({ conversation: decodeSegment(id) }).toString()}`
  }
  const appointment = new URLSearchParams(search).get("appointment")
  if (section === "appointments" && !id && appointment) {
    // The desk's canonical appointment link (ActivityResource uses it too):
    // opens the sheet on list clinics, lands on the calendar elsewhere.
    return `/appointments?${new URLSearchParams({ appointment }).toString()}`
  }
  return null
}

function decodeSegment(segment: string): string {
  try {
    return decodeURIComponent(segment)
  } catch {
    return segment
  }
}

/**
 * Desktop URL → the mobile route to land on. Never returns null. `search` is
 * the query string (with or without the leading "?"); it only matters for the
 * record-level links above.
 */
export function toMobileRoute(pathname: string, search = ""): string {
  return (
    toMobileDetail(pathname, search) ??
    longestPrefixMatch(pathname, DESKTOP_TO_MOBILE) ??
    MOBILE_HOME
  )
}

/**
 * False for desktop-only sections (hr, reports, settings, telephony…). Those
 * still land on `/m`, but the user gets told why instead of silently arriving
 * on a screen they didn't ask for.
 *
 * Note `/dashboard` and `/` DO have an equivalent (`/m`) — they map to home
 * intentionally, so a plain `toMobileRoute(p) === MOBILE_HOME` test would
 * mis-flag them.
 */
export function hasMobileEquivalent(pathname: string): boolean {
  return longestPrefixMatch(pathname, DESKTOP_TO_MOBILE) !== null
}

/** Remember that `pathname` had no mobile screen, for the toast on arrival. */
export function rememberOutOfScope(pathname: string): void {
  if (typeof window === "undefined") return
  try {
    window.sessionStorage.setItem(OUT_OF_SCOPE_KEY, pathname)
  } catch {
    /* no-op */
  }
}

/** Read-and-clear the pending out-of-scope path (one-shot). */
export function takeOutOfScope(): string | null {
  if (typeof window === "undefined") return null
  try {
    const value = window.sessionStorage.getItem(OUT_OF_SCOPE_KEY)
    if (value) window.sessionStorage.removeItem(OUT_OF_SCOPE_KEY)
    return value
  } catch {
    return null
  }
}

/** Mobile URL → the desktop route to land on. Never returns null. */
export function toDesktopRoute(pathname: string, search = ""): string {
  return (
    toDesktopDetail(pathname, search) ??
    longestPrefixMatch(pathname, MOBILE_TO_DESKTOP) ??
    "/"
  )
}

/**
 * True when the current viewport is phone-sized. SSR-safe: returns false on
 * the server (no window), so the desktop tree renders and the redirect runs
 * on mount instead of during render.
 */
export function isMobileViewport(): boolean {
  if (typeof window === "undefined") return false
  return window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`).matches
}

/** True when the user has opted out of the mobile app for this tab/session. */
export function isForceDesktop(): boolean {
  if (typeof window === "undefined") return false
  try {
    return window.sessionStorage.getItem(FORCE_DESKTOP_KEY) === "1"
  } catch {
    // Private-mode Safari throws on sessionStorage access. Treat as "not set".
    return false
  }
}

/** Set/clear the escape hatch. */
export function setForceDesktop(on: boolean): void {
  if (typeof window === "undefined") return
  try {
    if (on) window.sessionStorage.setItem(FORCE_DESKTOP_KEY, "1")
    else window.sessionStorage.removeItem(FORCE_DESKTOP_KEY)
  } catch {
    /* no-op */
  }
}
